import React, { useState } from 'react';
import ValidationError from './validation-error';
import {
    deselectNonInitialConnections,
    getNonInitialConnections
} from '../../../../../utils/connection-utils';
import { ConnectionModel } from '../../connection.model';
import QuickStartBlockedItems from '../../../wizard/policy-step/quick-start-blocked-items';
import { ECMActionType, LevelName} from '../../../../../utils/enums';
import CustomButton from '../../../../shared/custom-button/custom-button';
import PolicyNotesPopover from '../../../../shared/policy-notes-popover/policy-notes-popover';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import { useNavigate } from 'src/utils/use-navigate';
import { EventType } from '../../../../../dtos/analytics.dto';

interface QuickStartComponentProps {
    selectedConnections: Map<number, ConnectionModel>;
    setSelectedConnections?: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>;
    showToast: (actionType: ECMActionType, count: number) => void;
}

const QuickStartComponent: React.FC<QuickStartComponentProps> = observer(({ selectedConnections, setSelectedConnections, showToast }) => {
    const nonInitialConnections = getNonInitialConnections(selectedConnections);
    const isApplyDisabled = nonInitialConnections.length > 0;
    const navigate = useNavigate();

    const { customerStore } = useStores();
    const { customerId } = customerStore;

    const [isApplyLoading, setIsApplyLoading] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);

    const handleDeselectConnection = () => {
        deselectNonInitialConnections(selectedConnections, setSelectedConnections);
    };

    const handleEdit = async () => {
        const id = selectedConnections.keys().next().value;
        try {
            setIsEditLoading(true);
            customerStore.sendGeneralEvent(
                EventType.LinkClicked,
                {
                    button: 'edit(individual)',
                    number_of_sid: selectedConnections.size,
                    location: 'quickPolicyModal',
                }
            );
            await customerStore.setQuickPolicy([id], false);
            navigate(`/${customerId}/${id}/protection`, { replace: true });
        } catch {
            showToast(ECMActionType.QuickStart, 0);
        }
        finally {
            setIsEditLoading(false);
        }
    };

    const handleApply = async () => {
        const destinationServiceIdList = Array.from(selectedConnections.keys());
        try {
            setIsApplyLoading(true);
            customerStore.sendGeneralEvent(
                EventType.LinkClicked,
                {
                    button: 'applyPolicy',
                    number_of_sid: selectedConnections.size,
                    location: 'quickPolicyModal',
                }
            );
            await customerStore.setQuickPolicy(destinationServiceIdList, true);
            showToast(ECMActionType.QuickStart, destinationServiceIdList.length);
            setSelectedConnections?.(new Map());
        } catch {
            showToast(ECMActionType.QuickStart, 0);
        }
        finally {
            setIsApplyLoading(false);
        }
    };

    return (
        <div className="quick-start-component">
            <div className="action-type-content">
                {isApplyDisabled && (
                    <ValidationError
                        message="Quick Start can be applied to connections with “Initial” policy only."
                        deselectedConnections={nonInitialConnections}
                        onDeselectConnection={setSelectedConnections ? handleDeselectConnection : undefined} />
                )}
                <div className="action-type-title">Quick Start</div>
                <div className="action-type-desc">Quickly start your configuration</div>
                <p>
                    Protection from threats AT&T experts have curated for you,
                    prioritizing network traffic over deep policy creation and customization.
                    Quick Start applies protection against known high risk threats
                    over personalized policy protections.
                </p>
                <QuickStartBlockedItems serviceLevel={LevelName.GOLD} renderMode="flat" />
            </div>
            <div className="action-type-footer">
                <PolicyNotesPopover />
                <div className="action-type-footer-btn">
                    {selectedConnections.size === 1 &&
                        <CustomButton label="Edit" className={`secondary-btn action-btn ${isEditLoading ? 'loading' : ''}`}
                                  onClick={handleEdit}
                                  isDisabled={isApplyDisabled || isEditLoading} />
                    }
                    <CustomButton label="Apply" className={`primary-btn action-btn ${isApplyLoading ? 'loading' : ''}`}
                                  onClick={handleApply}
                                  isDisabled={selectedConnections.size === 0 || isApplyDisabled || isApplyLoading || isEditLoading}/>
                </div>
            </div>
        </div>
    );
});

export default QuickStartComponent;
